import type { Person } from '@/types'
import { cn, initials } from '@/lib/utils'

const sizes = {
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-10 w-10 text-sm',
  /* The account screen, where the photo is the subject rather than a label
     beside a name. Nothing else should reach for this. */
  xl: 'h-24 w-24 text-2xl',
} as const

export function Avatar({
  person,
  size = 'md',
  className,
}: {
  person: Pick<Person, 'firstName' | 'lastName' | 'avatarColor'> & { avatarUrl?: string | null } | undefined
  size?: keyof typeof sizes
  className?: string
}) {
  if (person?.avatarUrl) {
    return (
      <img
        src={person.avatarUrl}
        alt={`${person.firstName} ${person.lastName}`}
        loading="lazy"
        className={cn('inline-block shrink-0 rounded-full object-cover', sizes[size], className)}
        style={{ backgroundColor: person.avatarColor }}
      />
    )
  }

  if (!person) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground',
          sizes[size],
          className,
        )}
        aria-hidden
      >
        ?
      </span>
    )
  }
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-semibold', sizes[size], className)}
      style={{ backgroundColor: person.avatarColor, color: inkOn(person.avatarColor) }}
      title={`${person.firstName} ${person.lastName}`}
    >
      {initials(person.firstName, person.lastName)}
    </span>
  )
}

/**
 * Black or white initials, whichever can actually be read on that colour.
 *
 * They were always white. Measured across one church's people on 2026-10-14
 * the initials came out between 2.15:1 and 4.47:1 — every single one under
 * the 4.5 that 10px text needs, and the amber and lime ones close to
 * unreadable. It is not a palette problem: white cannot sit on half a colour
 * wheel, and these are identity colours that should not be darkened to suit
 * the text on top of them.
 *
 * It picks whichever ink has the BETTER contrast rather than switching at a
 * luminance threshold. A threshold is a guess about where the crossover is —
 * the first attempt used 0.45 and flipped exactly one colour of twelve,
 * leaving amber at 2.15 when black would have given it 9.77. Comparing the
 * two ratios needs no guess and is right by construction.
 *
 * The dark ink is pure black and the comparison says so. It was `#0b0b0c`
 * against a formula that assumed pure black, which is a small lie that cost
 * a real failure: indigo measured 4.40 in the browser where the arithmetic
 * had promised 4.67, and 4.5 is the line. Compare what you actually render.
 */
export function inkOn(background: string): string {
  const hex = background.replace('#', '')
  if (hex.length !== 6) return '#ffffff'

  const channel = (i: number) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  const l = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)

  const onWhite = 1.05 / (l + 0.05)
  const onBlack = (l + 0.05) / 0.05
  return onBlack >= onWhite ? '#000000' : '#ffffff'
}
